// Unit 07 — Subnetting (Design Lab → build your own plan on R1)
import { h } from '../../ui/h.js';
import { readConfirm, observe } from '../../ui/widgets.js';
import { pc, sw, router, link, network, setIf } from '../../engine/net/model.js';
import { isIp, ipToInt, intToIp, networkOf, broadcastOf, hostsForPrefix, prefixToMask, maskToPrefix } from '../../engine/net/addr.js';
import { ran, pinged, up, reach } from './helpers.js';

const BLOCK = { net: '172.16.40.0', prefix: 24 };
const REQ = [
  { id: 'sales', name: 'SALES', hosts: 50 },
  { id: 'eng', name: 'ENGINEERING', hosts: 26 },
  { id: 'mgmt', name: 'MANAGEMENT', hosts: 12 },
  { id: 'wan', name: 'WAN link to HQ', hosts: 2 },
];
const COLS = [['net', 'Network'], ['prefix', 'Prefix'], ['first', 'First usable'], ['last', 'Last usable'], ['bcast', 'Broadcast']];
const plus = (ip, n) => intToIp((ipToInt(ip) + n) >>> 0);

/** Validate one row → { ok, cells:{col:msg|null}, derived } */
function checkRow(r, need) {
  const cells = {};
  const p = Number(String(r.prefix || '').replace('/', ''));
  if (!isIp(r.net)) cells.net = 'Not a valid IPv4 address.';
  if (!Number.isInteger(p) || p < 1 || p > 30) cells.prefix = 'Enter a prefix length such as /27.';
  if (!cells.net && !cells.prefix) {
    if (networkOf(r.net, BLOCK.prefix) !== BLOCK.net || p < BLOCK.prefix) cells.net = 'Outside the 172.16.40.0/24 block.';
    else if (networkOf(r.net, p) !== r.net) cells.net = `Not a valid network address for a /${p} (it must sit on a block boundary).`;
    if (hostsForPrefix(p) < need) cells.prefix = `A /${p} holds only ${hostsForPrefix(p)} hosts.`;
    else if (p < 30 && hostsForPrefix(p + 1) >= need) cells.prefix = 'Wasteful: a smaller subnet still fits these hosts.';
  }
  if (!cells.net && !cells.prefix) {
    const b = broadcastOf(r.net, p);
    if (r.first !== plus(r.net, 1)) cells.first = 'Check the first usable address.';
    if (r.bcast !== b) cells.bcast = 'Check the broadcast address.';
    if (r.last !== plus(b, -1)) cells.last = 'Check the last usable address.';
  }
  return { ok: !Object.keys(cells).length, cells, p };
}
function overlaps(rows) {
  const bad = new Set();
  const iv = rows.map((r) => { const p = Number(String(r.prefix).replace('/', '')); return isIp(r.net) && p ? [ipToInt(r.net), ipToInt(broadcastOf(r.net, p))] : null; });
  iv.forEach((a, i) => iv.forEach((b, j) => { if (i < j && a && b && a[0] <= b[1] && b[0] <= a[1]) { bad.add(i); bad.add(j); } }));
  return bad;
}
export const planOf = (rt) => rt.widget('u07-plan').data.rows || {};
/** Check a whole plan (rows by segment id) → per-row results, with .overlap and .ok. */
function checkPlan(rowsById) {
  const rows = REQ.map((q) => (rowsById && typeof rowsById[q.id] === 'object' ? rowsById[q.id] : {}));
  const res = REQ.map((q, i) => checkRow(rows[i], q.hosts));
  const ov = overlaps(rows);
  ov.forEach((i) => { res[i].cells.net = res[i].cells.net || 'Overlaps another subnet.'; res[i].ok = false; });
  res.overlap = ov.size > 0;
  res.ok = res.every((r) => r.ok);
  return res;
}

function planWidget() {
  return {
    id: 'u07-plan', blocker: 'Complete a valid VLSM plan',
    verify: (d) => !!d?.rows && checkPlan(d.rows).ok && REQ.every((q) => !String(d.rows[q.id]?.prefix).includes('/')),
    mount(el, rt, api) {
      const s = api.state.data;
      s.rows = s.rows || Object.fromEntries(REQ.map((q) => [q.id, { net: '', prefix: '', first: '', last: '', bcast: '' }]));
      const render = (res) => {
        el.textContent = '';
        el.append(h('div', { class: 'w-title' }, 'VLSM PLAN · 172.16.40.0/24', api.state.done ? h('span', { class: 'ok' }, '✓ VALID PLAN') : null));
        const tbl = h('table', { class: 'tbl-plan' }, h('tr', {}, h('th', {}, 'Segment'), h('th', {}, 'Hosts'), COLS.map(([, l]) => h('th', {}, l))));
        REQ.forEach((q, i) => {
          const r = s.rows[q.id];
          const rr = res?.[i];
          tbl.append(h('tr', {}, h('td', {}, h('b', {}, q.name)), h('td', {}, q.hosts),
            COLS.map(([c]) => h('td', { class: rr ? (rr.cells[c] ? 'bad' : 'good') : '', title: rr?.cells[c] || '' },
              h('input', { value: r[c], disabled: api.state.done || null, placeholder: c === 'prefix' ? '/xx' : '', 'data-testid': `plan-${q.id}-${c}`, oninput: (e) => { r[c] = e.target.value.trim(); } })))));
        });
        el.append(tbl);
        if (res) {
          const msgs = [];
          res.forEach((rr, i) => { for (const [c, m] of Object.entries(rr.cells)) msgs.push(`${REQ[i].name} · ${COLS.find((x) => x[0] === c)[1]}: ${m}`); });
          if (res.overlap) msgs.push('Two or more subnets overlap. Each segment needs its own address range.');
          if (msgs.length) el.append(h('ul', { class: 'w-fb bad' }, msgs.map((m) => h('li', {}, m))));
        }
        if (!api.state.done) el.append(h('div', { class: 'row' }, h('button', { class: 'btn small', 'data-testid': 'plan-check', onclick: () => {
          const res2 = checkPlan(s.rows);
          const ok = res2.ok;
          rt.action('plan-check', { ok });
          if (ok) { s.rows = Object.fromEntries(REQ.map((q) => [q.id, { ...s.rows[q.id], prefix: String(s.rows[q.id].prefix).replace('/', '') }])); api.done(); }
          api.save();
          render(res2);
        } }, 'Validate plan')));
        else el.append(h('div', { class: 'w-fb good' }, 'Plan validated: every segment is right-sized, aligned, and non-overlapping.'));
      };
      render();
    },
  };
}

function net() {
  const R1 = router('R1', { seq: 2 });
  const SWS = sw('SW-SALES', { seq: 4 }), SWE = sw('SW-ENG', { seq: 5 }), SWM = sw('SW-MGMT', { seq: 6 });
  const S1 = pc('SALES1', { mac: '0050796a0101', dhcp: true });
  S1.ipcfg.dhcp = false; // blank static config awaiting your plan
  for (const s of [SWS, SWE, SWM]) setIf(s, 'GigabitEthernet0/1', { description: 'UPLINK-R1' });
  return network([R1, SWS, SWE, SWM, S1], [
    link('R1', 'GigabitEthernet0/0', 'SW-SALES', 'GigabitEthernet0/1'),
    link('R1', 'GigabitEthernet0/1', 'SW-ENG', 'GigabitEthernet0/1'),
    link('R1', 'GigabitEthernet0/2', 'SW-MGMT', 'GigabitEthernet0/1'),
    link('SW-SALES', 'FastEthernet0/1', 'SALES1', 'FastEthernet0'),
  ]);
}
const layout = (rt) => {
  const p = planOf(rt);
  const lab = (id) => (p[id]?.net && rt.widget('u07-plan').done ? `${p[id].net}/${p[id].prefix}` : 'your plan');
  return {
    width: 940, height: 360,
    nodes: {
      R1: { x: 470, y: 70, type: 'router', sub: 'BRANCH-02 router' },
      'SW-SALES': { x: 170, y: 210, type: 'switch', sub: `SALES · ${lab('sales')}` },
      'SW-ENG': { x: 470, y: 230, type: 'switch', sub: `ENGINEERING · ${lab('eng')}` },
      'SW-MGMT': { x: 770, y: 210, type: 'switch', sub: `MGMT · ${lab('mgmt')}` },
      SALES1: { x: 170, y: 320, type: 'pc', sub: '' },
    },
    zones: [{ x: 20, y: 20, w: 900, h: 330, label: 'BRANCH-02 · 172.16.40.0/24' }],
  };
};

export default {
  id: 'u07', num: '07', title: 'Subnetting', type: 'Design Lab', implemented: true,
  objective: 'Design a VLSM addressing plan from host requirements, then build it on a router and prove it works.',
  network: net, layout, adapterGui: true, linkLights: true,
  stages: [
    {
      id: 'learn', phase: 'LEARN', title: 'Subnetting without panic', workspace: false,
      content: `
<p>Subnetting is choosing <b>how many host bits</b> each segment needs, then carving the address block into aligned pieces.</p>
<h3>1 · Size it</h3>
<table><tr><th>Prefix</th><th>Mask</th><th>Block size</th><th>Usable hosts</th></tr>
<tr><td>/24</td><td>255.255.255.0</td><td>256</td><td>254</td></tr>
<tr><td>/25</td><td>255.255.255.128</td><td>128</td><td>126</td></tr>
<tr><td>/26</td><td>255.255.255.192</td><td>64</td><td>62</td></tr>
<tr><td>/27</td><td>255.255.255.224</td><td>32</td><td>30</td></tr>
<tr><td>/28</td><td>255.255.255.240</td><td>16</td><td>14</td></tr>
<tr><td>/29</td><td>255.255.255.248</td><td>8</td><td>6</td></tr>
<tr><td>/30</td><td>255.255.255.252</td><td>4</td><td>2 (point-to-point links)</td></tr></table>
<p>Usable hosts = 2<sup>host bits</sup> − 2 (network and broadcast). Pick the <b>smallest</b> block that fits.</p>
<h3>2 · Place it (VLSM)</h3>
<p>Allocate the <b>largest</b> subnet first, starting at the beginning of the block. Each subnet must start on a multiple of its block size.</p>
<pre>Example: 10.0.0.0/24 → 100 hosts (/25) + 20 hosts (/27)
  10.0.0.0/25    hosts .1 – .126    broadcast .127
  10.0.0.128/27  hosts .129 – .158  broadcast .159</pre>
<h3>3 · Derive the rest</h3>
<p>First usable = network + 1 · Broadcast = next network − 1 · Last usable = broadcast − 1.</p>`,
      widgets: [
        observe({
          id: 'u07-warm', title: 'WARM-UP',
          fields: [
            { label: 'Smallest prefix that fits 50 hosts', placeholder: '/xx', check: (v) => v.replace('/', '') === '26' || 'Find the smallest block whose usable hosts ≥ 50.' },
            { label: 'Broadcast address of 10.1.1.64/27', check: (v) => v === '10.1.1.95' || 'A /27 block is 32 addresses long.' },
          ],
        }),
      ],
      question: 'Why is allocating the largest subnet first the safest order?',
    },
    {
      id: 'design', phase: 'DESIGN', title: 'Design BRANCH-02', workspace: false,
      ticket: { id: '1007', priority: 4, from: 'Network Architecture', site: 'BRANCH-02', subject: 'Addressing plan for new branch', body: 'Block allocated: 172.16.40.0/24. Segments: SALES 50 hosts, ENGINEERING 26, MANAGEMENT 12, and a point-to-point WAN link to HQ. Right-size every subnet — we will add more segments later.' },
      widgets: [planWidget()],
      question: 'After your four subnets, how much of the /24 is still free for future segments?',
      hints: ['Order by size: SALES, ENGINEERING, MANAGEMENT, WAN. Start SALES at 172.16.40.0.', 'SALES needs a /26 (64 addresses) → the next free address is 172.16.40.64.', 'ENGINEERING /27 at .64, MANAGEMENT /28 at .96, WAN /30 at .112.'],
    },
    {
      id: 'build', phase: 'BUILD → VERIFY', title: 'Build your plan on R1', devices: ['R1', 'SALES1'],
      content: (rt) => {
        const p = planOf(rt);
        const row = (id, name, ifc) => `<tr><td>${ifc}</td><td>${name}</td><td>${p[id]?.first || '?'} ${p[id]?.prefix ? prefixToMask(+p[id].prefix) : ''}</td></tr>`;
        return `<p>Use the <b>first usable</b> address of each subnet from <i>your</i> plan as the router interface (the gateway). Give SALES1 the <b>last usable</b> SALES address.</p>
<table><tr><th>R1 interface</th><th>Segment</th><th>Address (from your plan)</th></tr>${row('sales', 'SALES', 'Gi0/0')}${row('eng', 'ENGINEERING', 'Gi0/1')}${row('mgmt', 'MANAGEMENT', 'Gi0/2')}</table>
<p>SALES1: <code>${p.sales?.last || '?'}</code> / <code>${p.sales?.prefix ? prefixToMask(+p.sales.prefix) : '?'}</code>, gateway <code>${p.sales?.first || '?'}</code>.</p>`;
      },
      evidence: [
        { id: 'route', text: 'R1: routing table shows three connected subnets', match: (ev) => ran('R1', 'show ip route')(ev) && (ev.output.match(/^C\s+/gm) || []).length >= 3 },
        { id: 'brief', text: 'R1: interface summary shows three interfaces up/up', match: (ev) => ran('R1', 'show ip interface brief')(ev) && (ev.output.match(/YES manual up\s+up/g) || []).length >= 3 },
        { id: 'ping', text: 'SALES1: gateway answers', match: (ev, rt) => pinged('SALES1', planOf(rt).sales?.first, { success: true })(ev) },
      ],
      tasks: ['sales', 'eng', 'mgmt'].map((id, i) => ({
        id, text: `R1 Gi0/${i} = first usable ${id.toUpperCase()} address, correct mask, up/up`,
        check: (rt) => { const p = planOf(rt)[id]; const ifc = rt.net.devices.R1.interfaces[`GigabitEthernet0/${i}`]; return ifc.ip?.ip === p.first && maskToPrefix(ifc.ip?.mask) === +p.prefix && up(rt, 'R1', `GigabitEthernet0/${i}`); },
        fail: 'Interface does not match your plan, or is not up/up.',
      })).concat([{
        id: 'host', text: 'SALES1 uses the last usable SALES address and reaches its gateway',
        check: (rt) => { const p = planOf(rt).sales; const c = rt.net.devices.SALES1.ipcfg; return c.ip === p.last && maskToPrefix(c.mask) === +p.prefix && c.gw === p.first && reach(rt, 'SALES1', p.first); },
        fail: 'SALES1 is not configured from your plan or cannot reach its gateway.',
      }]),
      question: 'How does R1 know about all three subnets without a single routing command?',
      hints: ['Each interface: <code>interface g0/0</code> → <code>ip address &lt;first&gt; &lt;mask&gt;</code> → <code>no shutdown</code>.', 'The mask for a /26 is 255.255.255.192; /27 is .224; /28 is .240.', 'Configure SALES1 with the ⚙ adapter settings button or netsh.'],
    },
  ],
  review: (rt) => {
    const p = planOf(rt);
    return `<p>Your plan: ${['sales', 'eng', 'mgmt', 'wan'].map((k) => `<code>${p[k]?.net}/${p[k]?.prefix}</code>`).join(' · ')}. It uses ${[64, 32, 16, 4].reduce((a, b) => a + b)} of 256 addresses; everything else in the /24 stays free for growth. R1 learned every subnet as a <b>connected</b> route the moment each interface came up.</p>`;
  },
};
